import { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import { Timeline } from '../../../../components/History/TimeLine';
import { TimelineIcon } from '../../../../components/History/TimeLineIcon';
import CustomAccordion from '../../../../components/Accordion/CustomAccordion';
import { TranslateFunction } from '../../../../hooks/useTranslate';
import { Visita } from '../../consts/visitasTypes';
import { useGetLogAuditoriaVisitaQuery, useGetPreventorsQuery } from '../../../../services/visitasApi';
import { formatFullName } from '../../../../utils/common';

const TIPO_EVENTO_ICON_PROPS: Record<string, { borderColor: string; icon: React.ReactNode }> = {
    VISITA_MARCADA_FALLIDA: {
        borderColor: '#D97706',
        icon: <WarningAmberOutlinedIcon />
    },
    VISITA_APROBADA: {
        borderColor: '#16A34A',
        icon: <CheckCircleOutlineOutlinedIcon />
    },
    VISITA_DEVUELTA: {
        borderColor: '#2563EB',
        icon: <UndoOutlinedIcon />
    },
    VISITA_SOLICITUD_CAMBIOS: {
        borderColor: '#9333EA',
        icon: <EditOutlinedIcon />
    },
    VISITA_CANCELADA: {
        borderColor: '#DC2626',
        icon: <CancelOutlinedIcon />
    }
};

const formatFechaHora = (fecha: string) => {
    const dateObj = new Date(fecha);
    const date = dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    const time = dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return `${date} ${time}`;
};

const EventoDetalle = ({
    tipoEvento,
    detalle,
    translate,
    preventoresPorId
}: {
    tipoEvento: string;
    detalle: Record<string, any> | null;
    translate: TranslateFunction;
    preventoresPorId: Record<number, string>;
}) => {
    if (!detalle) return null;

    const nombrePreventor = (idPreventor: number | null | undefined) => {
        if (idPreventor === null || idPreventor === undefined) return '-';
        return preventoresPorId[idPreventor] ?? `#${idPreventor}`;
    };

    if (tipoEvento === 'VISITA_MARCADA_FALLIDA') {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {translate('history.detail.motivo')}
                </Typography>
                <Typography mb={2}>{detalle.motivoDescripcion || '-'}</Typography>
                {/* SMP-179: el destino (Cerrada o Pendiente) depende del motivo seleccionado */}
                <Typography variant="body2" color="text.secondary">
                    {translate('history.detail.estadoDestino')}
                </Typography>
                <Typography mb={detalle.observacion ? 2 : 0}>
                    {detalle.estadoDestino || '-'}
                </Typography>
                {detalle.observacion && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {translate('history.detail.observacion')}
                        </Typography>
                        <Typography>{detalle.observacion}</Typography>
                    </>
                )}
            </Box>
        );
    }

    if (tipoEvento === 'VISITA_APROBADA') {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {translate('history.detail.estadoDestino')}
                </Typography>
                <Typography>{detalle.estadoDestino || '-'}</Typography>
            </Box>
        );
    }

    if (tipoEvento === 'VISITA_DEVUELTA') {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                            {translate('history.detail.preventorAnterior')}
                        </Typography>
                        <Typography>{nombrePreventor(detalle.preventorAnterior)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                            {translate('history.detail.preventorNuevo')}
                        </Typography>
                        <Typography>{nombrePreventor(detalle.preventorNuevo)}</Typography>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" mt={2}>
                    {translate('history.detail.estadoDestino')}
                </Typography>
                <Typography mb={detalle.nota ? 2 : 0}>{detalle.estadoDestino || '-'}</Typography>
                {detalle.nota && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {translate('history.detail.nota')}
                        </Typography>
                        <Typography>{detalle.nota}</Typography>
                    </>
                )}
            </Box>
        );
    }

    if (tipoEvento === 'VISITA_SOLICITUD_CAMBIOS') {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {translate('history.detail.observacion')}
                </Typography>
                <Typography>{detalle.observacion || '-'}</Typography>
            </Box>
        );
    }

    if (tipoEvento === 'VISITA_CANCELADA') {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {translate('history.detail.observacion')}
                </Typography>
                <Typography>{detalle.observacion || '-'}</Typography>
            </Box>
        );
    }

    return null;
};

interface VisitHistoryDrawerProps {
    translate: TranslateFunction;
    selected: Visita | null;
    closeDrawer: () => void;
}

const VisitHistoryDrawer = ({ translate, selected, closeDrawer }: VisitHistoryDrawerProps) => {
    const idVisita = selected?.id;
    const { data, isFetching } = useGetLogAuditoriaVisitaQuery(
        { idVisita: idVisita as number },
        { skip: !idVisita }
    );

    const eventos = data?.data ?? [];
    const hayEventos = eventos.length > 0;
    const establecimientoNombre = (selected as any)?.establecimiento?.nombre || '';

    const hasDevueltaEvent = eventos.some((evento: any) => evento.tipoEvento === 'VISITA_DEVUELTA');
    const { data: preventoresResponse } = useGetPreventorsQuery({}, { skip: !hasDevueltaEvent });
    const preventoresPorId = useMemo(() => {
        const preventores = preventoresResponse?.data || [];
        return preventores.reduce((acc: Record<number, string>, p: any) => {
            acc[p.idUsuario] = formatFullName(p.nombre, p.apellido);
            return acc;
        }, {});
    }, [preventoresResponse]);

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                padding={1}
                sx={{ flexDirection: 'column', height: '100%' }}
            >
                <Grid position="relative">
                    <DrawerHeader onIconClick={closeDrawer} text={translate('history.drawerTitle')} />
                    <Typography
                        position="absolute"
                        color="#94A3B8"
                        fontSize="1rem"
                        top={65}
                        left={42}
                    >
                        {`#${idVisita ?? ''}`}
                        {establecimientoNombre ? ` • ${establecimientoNombre}` : ''}
                    </Typography>
                    <Grid mt={4}>
                        {!hayEventos && !isFetching && (
                            <Box
                                width="100%"
                                height="calc(100vh / 1.5)"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Typography align="center" width="100%">
                                    {translate('history.emptyState')}
                                </Typography>
                            </Box>
                        )}
                        <Timeline>
                            {eventos.map((evento: any) => (
                                <Box display="flex" key={evento.id}>
                                    <TimelineIcon
                                        borderColor={
                                            TIPO_EVENTO_ICON_PROPS[evento.tipoEvento]
                                                ?.borderColor || '#E2E8F0'
                                        }
                                        backgroundColor="#FFFFFF"
                                        icon={
                                            TIPO_EVENTO_ICON_PROPS[evento.tipoEvento]?.icon ?? null
                                        }
                                    />
                                    <Box flex={1} pl={2} pb={2}>
                                        <CustomAccordion
                                            seccion={{
                                                id: evento.id,
                                                titulo: (
                                                    <Box component="span">
                                                        <Typography
                                                            component="span"
                                                            display="block"
                                                            fontWeight={700}
                                                        >
                                                            {evento.resumen}
                                                        </Typography>
                                                        <Typography
                                                            component="span"
                                                            display="block"
                                                            color="#667085"
                                                            fontSize=".9rem"
                                                        >
                                                            {formatFechaHora(evento.fecha)} •{' '}
                                                            {evento.actor?.nombre} (
                                                            {evento.actor?.rol})
                                                        </Typography>
                                                    </Box>
                                                )
                                            }}
                                            initiallyExpanded={false}
                                            summaryBgColor="#F3F4F6"
                                            summaryColor="#101828"
                                            Children={
                                                <EventoDetalle
                                                    tipoEvento={evento.tipoEvento}
                                                    detalle={evento.detalle}
                                                    translate={translate}
                                                    preventoresPorId={preventoresPorId}
                                                />
                                            }
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Timeline>
                    </Grid>
                </Grid>
            </Box>
            <BackdropComponent loading={isFetching} />
        </>
    );
};

export { VisitHistoryDrawer };
